import { TooltipWrapper } from 'components/TooltipWrapper';
import { Checkbox } from 'uikit';

import type React from 'react';
import { useCallback, useContext, useMemo, useState } from 'react';

import type {
    ITableLikeTreeCheckConfirmation,
    ITableLikeTreeCheckConfirmationItem,
    ITableLikeTreeItem,
} from '../context';
import { TableLikeTreeContext } from '../context';
import { getAllChildren } from '../utils';

import Style from './TreeItemCheckBox.module.css';

interface ITreeItemCheckBox {
    treeItem: ITableLikeTreeItem;
}

export const TreeItemCheckBox: React.FC<ITreeItemCheckBox> = (props) => {
    const { treeItem } = props;
    const context = useContext(TableLikeTreeContext);
    const {
        onCheckChange,
        checkedIds,
        areCheckboxesDisabled,
        checkConfirmations,
        disabledCheckBoxesItemsIds,
        checkBoxesNotifications,
    } = context;

    const { onCheck, onUncheck } = useMemo(
        () => checkConfirmations?.find((c) => c.treeItemId === treeItem.id) || ({} as ITableLikeTreeCheckConfirmation),
        [checkConfirmations, treeItem.id],
    );

    const checkBoxNotificationMessage = useMemo(
        () => checkBoxesNotifications?.find((c) => c.treeItemId === treeItem.id)?.message,
        [checkBoxesNotifications, treeItem.id],
    );

    const [isCheckOrUncheckTooltipVisible, setIsCheckOrUncheckTooltipVisible] = useState(false);
    const [confirmingState, setConfirmingState] = useState<boolean>();

    const isChecked = useMemo(() => checkedIds.includes(treeItem.id), [checkedIds, treeItem.id]);

    const { message, confirmLabel, cancelLabel } = useMemo(() => {
        if (isCheckOrUncheckTooltipVisible) {
            return (confirmingState ? onCheck : onUncheck) || ({} as ITableLikeTreeCheckConfirmationItem);
        }

        return { message: '', confirmLabel: '', cancelLabel: '' };
    }, [confirmingState, isCheckOrUncheckTooltipVisible, onCheck, onUncheck]);

    const isIntermediate = useMemo(
        () =>
            isChecked &&
            !getAllChildren(treeItem)
                .map((i) => i.id)
                .every((i) => checkedIds.includes(i)),
        [checkedIds, isChecked, treeItem],
    );

    const isDisabled = useMemo(
        () => areCheckboxesDisabled || disabledCheckBoxesItemsIds?.includes(treeItem.id),
        [areCheckboxesDisabled, disabledCheckBoxesItemsIds, treeItem.id],
    );

    const onChangeLocal = useCallback(
        ({ checked }) => {
            if ((checked && onCheck) || (!checked && onUncheck)) {
                setConfirmingState(checked);
                setIsCheckOrUncheckTooltipVisible(true);
            } else {
                onCheckChange(treeItem, checked);
            }
        },
        [onCheck, onCheckChange, onUncheck, treeItem],
    );

    const reset = useCallback(() => {
        setConfirmingState(undefined);
        setIsCheckOrUncheckTooltipVisible(false);
    }, []);

    const onConfirm = useCallback(() => {
        onCheckChange(treeItem, confirmingState as boolean);
        reset();
    }, [confirmingState, onCheckChange, reset, treeItem]);

    return (
        <TooltipWrapper
            message={message || checkBoxNotificationMessage}
            isConfirm={!checkBoxNotificationMessage}
            confirmLabel={confirmLabel}
            cancelLabel={cancelLabel}
            isHidden={!checkBoxNotificationMessage && !isCheckOrUncheckTooltipVisible}
            onConfirm={onConfirm}
            onCancel={reset}
            size="m"
            className={Style.treeItemCheckBox}
        >
            <Checkbox
                checked={isChecked}
                onChange={onChangeLocal}
                disabled={isDisabled}
                intermediate={isIntermediate}
            />
        </TooltipWrapper>
    );
};

TreeItemCheckBox.displayName = 'TreeItemCheckBox';
